// ─── public.agent_lead_decisions → "what did the agent do about this lead?" ────
//
// WHY THIS EXISTS
// Every agent first contact is triggered by ONE Supabase database webhook, which
// has a 5s timeout, no retries, and no history in net._http_response. When it
// drops a call the lead is never texted AND nothing records that a decision was
// ever taken. agent_lead_decisions (recruiter-agent/db/2026-09-02-lead-decisions.sql)
// is the record: one row per first-contact CONSIDERATION, from the webhook and
// from the */15 sweeper.
//
// The table has existed since 2026-09-02 and the CRM has never read it. So the
// question a recruiter actually asks about a lead — "did we try, and what
// happened?" — was answerable only by opening the Supabase SQL editor. The
// absence of a row is itself the diagnosis: a lead NOTHING considered was never
// seen by anything, which is the failure the table was built to make visible.
//
// READ-ONLY. The CRM never writes here. The log is the worker's account of its
// own behaviour, and a CRM that could edit it would make it evidence of nothing.
//
// NO PII BY CONSTRUCTION: the table holds lead_id, source, decision, reason and
// a timestamp, all built from literals in the worker. Nothing here needs to
// redact anything, and nothing here should ever start displaying a lead field
// alongside them that the table itself refused to store.
//
// PURE ON PURPOSE — see ./messageStatus and ./leadClassification.

/**
 * Group decision rows by lead_id, newest first within each lead.
 *
 * lead_id is TEXT in that table (matching agent_conversations.candidate_id) while
 * leads.id is a uuid, so both sides are compared as strings. A row with no
 * lead_id belongs to no lead and is dropped: it cannot be attributed, and
 * attributing it to the wrong lead would be worse than not showing it.
 */
export function decisionsByLead(rows) {
  const out = new Map();
  (rows || []).forEach((r) => {
    if (!r || r.lead_id == null || r.lead_id === '') return;
    const key = String(r.lead_id);
    const list = out.get(key) || [];
    list.push(r);
    out.set(key, list);
  });
  for (const list of out.values()) {
    list.sort((a, b) => {
      const ta = a.created_at ? Date.parse(a.created_at) : NaN;
      const tb = b.created_at ? Date.parse(b.created_at) : NaN;
      // Unparseable timestamps sort last rather than reordering everything
      // around them via NaN comparisons.
      const na = Number.isNaN(ta) ? -Infinity : ta;
      const nb = Number.isNaN(tb) ? -Infinity : tb;
      return nb - na;
    });
  }
  return out;
}

/** The decision rows for one lead, newest first; [] when nothing considered it. */
export function decisionsFor(index, leadId) {
  if (!index || leadId == null) return [];
  return index.get(String(leadId)) || [];
}

// The three outcomes a reader has to tell apart at a glance. Anything not listed
// keeps the neutral style and shows its own literal text — an unrecognised
// decision is information, and recolouring it to a guess would be a lie about a
// vocabulary this module does not own.
const TONE = {
  sent: 'good',
  escalated: 'attention',
  would_send: 'attention',
  gated: 'neutral',
  excluded: 'neutral',
  skipped: 'neutral',
  failed: 'bad',
};

/**
 * Presentation for one decision row: its literal decision and reason, plus a
 * tone in { good | attention | bad | neutral }.
 *
 * The decision text is ALWAYS the label. Colour is an aid to scanning, never a
 * replacement for the value — the same rule formTypePillFor() follows.
 */
export function decisionDisplay(row) {
  const decision = (row && row.decision ? String(row.decision) : '').trim();
  const reason = (row && row.reason ? String(row.reason) : '').trim();
  const source = (row && row.source ? String(row.source) : '').trim();
  return {
    label: decision || '—',
    reason,
    source,
    at: (row && row.created_at) || null,
    tone: TONE[decision.toLowerCase()] || 'neutral',
  };
}

/**
 * The one-line summary shown when the decisions are collapsed.
 *
 * An empty list is NOT "no decisions" — it is the diagnosis. Says so, because a
 * recruiter reading "nothing considered this lead" knows to escalate, whereas
 * "0 decisions" reads like a tidy zero.
 */
export function decisionsSummary(rows) {
  const list = rows || [];
  if (list.length === 0) return 'nothing considered this lead';
  const n = list.length;
  return `${n} decision${n === 1 ? '' : 's'}`;
}
